import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ChevronDown, Check, Plus, Building2, Sparkles, Users as UsersIcon, TrendingUp } from "lucide-react";

/**
 * WorkspaceSwitcher — shows the current workspace with an optional dropdown
 * to switch to any workspace the user belongs to, or create a new one.
 * When `collapsed`, shows just the initial + a badge; the dropdown still works.
 */
export default function WorkspaceSwitcher({ collapsed = false }) {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [snapshots, setSnapshots] = useState({});
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const rootRef = useRef(null);

  const load = () => {
    api.get("/workspaces/mine").then(({ data }) => {
      setRows(data?.workspaces || []);
    }).catch(() => {});
    api.get("/workspaces/mine/snapshot").then(({ data }) => {
      const map = {};
      (data?.snapshots || []).forEach((s) => { map[s.workspace_id] = s; });
      setSnapshots(map);
    }).catch(() => {});
  };

  useEffect(load, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const activeId = user?.active_workspace_id || user?.id;
  const current = rows.find((r) => r.workspace_id === activeId) || rows[0];

  const doSwitch = async (wsId) => {
    if (wsId === activeId) { setOpen(false); return; }
    try {
      await api.post("/workspaces/switch", { workspace_id: wsId });
      await refreshMe();
      setOpen(false);
      toast.success(`Switched workspace`);
      // Hard reload so any in-memory caches (contacts list, pipelines etc.) refetch
      window.location.reload();
    } catch (e) {
      toast.error("Failed to switch");
    }
  };

  const doCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Give the workspace a name"); return; }
    try {
      await api.post("/workspaces", { name: trimmed });
      await refreshMe();
      toast.success(`Created "${trimmed}"`);
      setName("");
      setCreating(false);
      setOpen(false);
      window.location.reload();
    } catch {
      toast.error("Failed to create workspace");
    }
  };

  const displayName = current?.name || user?.workspace_name || "PEAK";
  const displayLogo = current?.logo || user?.workspace_logo;

  return (
    <div ref={rootRef} className="relative" data-testid="workspace-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-switcher-btn"
        className={`w-full flex items-center gap-3 min-w-0 text-left hover:bg-[var(--peak-surface-alt)] transition-colors ${collapsed ? "justify-center" : "px-1 py-1"}`}
      >
        {displayLogo ? (
          <img
            src={displayLogo}
            alt="Workspace logo"
            className="w-8 h-8 object-cover border border-[var(--peak-ink)] flex-none"
            data-testid="workspace-logo"
          />
        ) : (
          <div className="w-8 h-8 bg-[var(--peak-accent)] flex items-center justify-center flex-none">
            <span className="text-[var(--peak-accent-contrast)] font-black text-lg font-heading">
              {(displayName || "P").charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-heading font-black tracking-tighter text-base leading-none truncate text-[var(--peak-ink)]" data-testid="workspace-name">
              {(displayName || "PEAK").toUpperCase()}
            </div>
            <div className="section-label leading-none mt-1 truncate">
              {current?.role === "owner" ? "Owner · Peak CRM" : `${(current?.role || "member").toUpperCase()} · Peak CRM`}
            </div>
          </div>
        )}
        {!collapsed && rows.length > 1 && (
          <ChevronDown className="w-4 h-4 text-[var(--peak-muted)] flex-none" strokeWidth={2.5} />
        )}
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-2 z-50 bg-[var(--peak-surface)] border-2 border-[var(--peak-ink)] shadow-[4px_4px_0_0_var(--peak-shadow)] max-h-[70vh] overflow-y-auto"
          data-testid="workspace-switcher-dropdown"
          style={{ minWidth: collapsed ? 220 : undefined }}
        >
          <div className="section-label px-3 pt-3 pb-2">Your workspaces</div>
          {rows.map((w) => {
            const active = w.workspace_id === activeId;
            const snap = snapshots[w.workspace_id];
            const stateChip = snap ? (
              snap.state === "trialing" && snap.days_left != null ? { label: `Trial · ${snap.days_left}d left`, cls: (snap.days_left <= 3 ? "text-[#E50000] bg-white" : "text-[var(--peak-accent)] bg-white") }
              : snap.state === "paid" ? { label: `${(snap.plan || "").toUpperCase()} · Paid`, cls: "text-[#008A27] bg-white" }
              : snap.state === "expired" ? { label: "Trial ended", cls: "text-[#E50000] bg-white" }
              : snap.state === "cancelling" ? { label: "Cancelling", cls: "text-[var(--peak-muted)] bg-white" }
              : snap.state === "comped" ? { label: "Comped", cls: "text-[#008A27] bg-white" }
              : { label: "Free", cls: "text-[var(--peak-muted)] bg-white" }
            ) : null;
            // Budget bar tone
            const pct = snap?.expense_budget_pct;
            const barTone = pct == null ? null
              : pct >= 100 ? { fillCls: "bg-[#E50000]", textCls: "text-[#E50000]" }
              : pct >= 80 ? { fillCls: "bg-[#F0AD00]", textCls: "text-[#F0AD00]" }
              : { fillCls: "bg-[var(--peak-accent)]", textCls: "text-[var(--peak-muted)]" };
            return (
              <button
                key={w.workspace_id}
                type="button"
                onClick={() => doSwitch(w.workspace_id)}
                data-testid={`ws-option-${w.workspace_id}`}
                data-active={active ? "1" : "0"}
                className={`w-full flex items-start gap-3 px-3 py-2.5 text-left border-t border-[var(--peak-border)] transition-colors ${
                  active
                    ? "bg-[var(--peak-surface-alt)]"
                    : "hover:bg-[var(--peak-surface-alt)]"
                }`}
              >
                {w.logo ? (
                  <img src={w.logo} alt="" className="w-6 h-6 object-cover border border-[var(--peak-ink)] flex-none mt-0.5" />
                ) : (
                  <div className="w-6 h-6 bg-[var(--peak-accent)] flex items-center justify-center flex-none mt-0.5">
                    <span className="text-[var(--peak-accent-contrast)] font-black text-xs font-heading">
                      {(w.name || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-bold tracking-tight text-sm leading-tight truncate text-[var(--peak-ink)] flex-1 min-w-0">
                      {w.name}
                    </div>
                    {stateChip && (
                      <span
                        data-testid={`ws-state-${w.workspace_id}`}
                        className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[var(--peak-border)] ${stateChip.cls}`}
                      >
                        {stateChip.label}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-0.5 truncate">
                    {w.role}
                  </div>
                  {snap && (
                    <div className="mt-1.5 flex items-center gap-3 text-[10px] font-mono text-[var(--peak-muted)]" data-testid={`ws-snapshot-${w.workspace_id}`}>
                      <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" strokeWidth={2.5} />{snap.contacts}</span>
                      <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" strokeWidth={2.5} />${snap.pipeline_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  )}
                  {snap && snap.expense_budget_monthly && barTone && (
                    <div className="mt-2" data-testid={`ws-budget-${w.workspace_id}`}>
                      <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-widest">
                        <span className="text-[var(--peak-muted)]">Expense budget</span>
                        <span className={barTone.textCls}>
                          ${snap.expenses_this_month.toLocaleString(undefined, { maximumFractionDigits: 0 })} / ${snap.expense_budget_monthly.toLocaleString(undefined, { maximumFractionDigits: 0 })} · {pct}%
                        </span>
                      </div>
                      <div className="h-1 bg-[var(--peak-border)] mt-1 relative overflow-hidden">
                        <div
                          className={`h-full ${barTone.fillCls}`}
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {active && (
                  <div className="flex-none w-5 h-5 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] flex items-center justify-center mt-0.5">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })}
          {creating ? (
            <div className="border-t-2 border-[var(--peak-ink)] p-3 flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doCreate(); if (e.key === "Escape") { setCreating(false); setName(""); } }}
                placeholder="New business name"
                data-testid="ws-name-input"
                className="brutalist-input flex-1"
              />
              <button
                type="button"
                onClick={doCreate}
                data-testid="ws-create-save"
                className="px-3 py-1.5 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] font-heading font-bold uppercase text-xs tracking-wider"
              >
                Add
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setCreating(true)}
                data-testid="ws-create-btn"
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left border-t-2 border-[var(--peak-ink)] hover:bg-[var(--peak-surface-alt)] text-[var(--peak-ink)]"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                <span className="font-heading font-bold uppercase text-xs tracking-wider">New business</span>
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/workspaces"); }}
                data-testid="ws-manage-btn"
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left border-t border-[var(--peak-border)] hover:bg-[var(--peak-surface-alt)] text-[var(--peak-muted)]"
              >
                <Building2 className="w-4 h-4" strokeWidth={2} />
                <span className="font-mono uppercase text-xs tracking-wider">Manage workspaces</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
